import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Page,
  Layout,
  Card,
  BlockStack,
  InlineStack,
  Text,
  Button,
  Select,
  Spinner,
  Banner,
  DataTable,
  Box,
} from '@shopify/polaris';

import { useAdminAuth } from '../../providers/AdminAuthProvider';

export default function AdminAnalytics() {
  const navigate = useNavigate();
  const { isAuthenticated, adminFetch } = useAdminAuth();
  const [analytics, setAnalytics] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [days, setDays] = useState('30');

  const loadAnalytics = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await adminFetch(`/api/admin/analytics?days=${days}`);
      if (response.ok) {
        const data = await response.json();
        setAnalytics(data.analytics);
      } else {
        setError('Failed to load analytics');
      }
    } catch (err) {
      setError('Failed to load analytics');
    } finally {
      setIsLoading(false);
    }
  }, [adminFetch, days]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin');
      return;
    }
    loadAnalytics();
  }, [isAuthenticated, navigate, loadAnalytics]);

  if (!isAuthenticated) {
    return null;
  }

  // Calculate totals from daily data
  const totals = analytics?.daily?.reduce(
    (acc, day) => ({
      impressions: acc.impressions + (day.impressions || 0),
      clicks: acc.clicks + (day.clicks || 0),
      conversions: acc.conversions + (day.conversions || 0),
      revenue: acc.revenue + (day.revenue || 0),
    }),
    { impressions: 0, clicks: 0, conversions: 0, revenue: 0 }
  ) || { impressions: 0, clicks: 0, conversions: 0, revenue: 0 };

  const dailyRows = analytics?.daily?.slice().reverse().slice(0, 14).map(day => [
    day.date,
    day.impressions?.toLocaleString() || 0,
    day.clicks?.toLocaleString() || 0,
    day.conversions?.toLocaleString() || 0,
    `$${day.revenue?.toFixed(2) || '0.00'}`,
    day.impressions > 0 ? `${((day.clicks / day.impressions) * 100).toFixed(2)}%` : '0%',
    day.impressions > 0 ? `${((day.conversions / day.impressions) * 100).toFixed(2)}%` : '0%',
  ]) || [];

  const topStoresRows = analytics?.top_stores?.map(store => [
    store.shop_domain,
    store.impressions?.toLocaleString() || 0,
    store.conversions?.toLocaleString() || 0,
    `$${store.revenue?.toFixed(2) || '0.00'}`,
  ]) || [];

  const topBarsRows = analytics?.top_bars?.map(bar => [
    bar.name,
    bar.shop_domain,
    bar.impressions?.toLocaleString() || 0,
    bar.conversions?.toLocaleString() || 0,
  ]) || [];

  return (
    <Page
      title="Global Analytics"
      backAction={{ content: 'Dashboard', onAction: () => navigate('/admin/dashboard') }}
    >
      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="critical" onDismiss={() => setError(null)}>
              {error}
            </Banner>
          </Layout.Section>
        )}

        {/* Period Selector */}
        <Layout.Section>
          <Card>
            <InlineStack gap="400" align="end">
              <div style={{ width: '200px' }}>
                <Select
                  label="Time Period"
                  options={[
                    { label: 'Last 7 days', value: '7' },
                    { label: 'Last 14 days', value: '14' },
                    { label: 'Last 30 days', value: '30' },
                    { label: 'Last 60 days', value: '60' },
                    { label: 'Last 90 days', value: '90' },
                  ]}
                  value={days}
                  onChange={setDays}
                />
              </div>
              <Button onClick={loadAnalytics}>Refresh</Button>
            </InlineStack>
          </Card>
        </Layout.Section>

        {isLoading ? (
          <Layout.Section>
            <Card>
              <Box padding="800">
                <InlineStack align="center">
                  <Spinner size="large" />
                </InlineStack>
              </Box>
            </Card>
          </Layout.Section>
        ) : (
          <>
            {/* Summary Cards */}
            <Layout.Section>
              <InlineStack gap="400" wrap={false}>
                <div style={{ flex: 1 }}>
                  <Card>
                    <BlockStack gap="200">
                      <Text variant="headingMd">Impressions</Text>
                      <Text variant="heading2xl">{totals.impressions.toLocaleString()}</Text>
                    </BlockStack>
                  </Card>
                </div>
                <div style={{ flex: 1 }}>
                  <Card>
                    <BlockStack gap="200">
                      <Text variant="headingMd">Clicks</Text>
                      <Text variant="heading2xl">{totals.clicks.toLocaleString()}</Text>
                      <Text tone="subdued">
                        CTR: {totals.impressions > 0 ? ((totals.clicks / totals.impressions) * 100).toFixed(2) : 0}%
                      </Text>
                    </BlockStack>
                  </Card>
                </div>
                <div style={{ flex: 1 }}>
                  <Card>
                    <BlockStack gap="200">
                      <Text variant="headingMd">Conversions</Text>
                      <Text variant="heading2xl">{totals.conversions.toLocaleString()}</Text>
                      <Text tone="subdued">
                        CVR: {totals.impressions > 0 ? ((totals.conversions / totals.impressions) * 100).toFixed(2) : 0}%
                      </Text>
                    </BlockStack>
                  </Card>
                </div>
                <div style={{ flex: 1 }}>
                  <Card>
                    <BlockStack gap="200">
                      <Text variant="headingMd">Revenue Influenced</Text>
                      <Text variant="heading2xl">${totals.revenue.toLocaleString()}</Text>
                    </BlockStack>
                  </Card>
                </div>
              </InlineStack>
            </Layout.Section>

            {/* Daily Breakdown */}
            <Layout.Section>
              <Card>
                <BlockStack gap="400">
                  <Text variant="headingMd">Daily Breakdown (Last 14 Days)</Text>
                  {dailyRows.length > 0 ? (
                    <DataTable
                      columnContentTypes={['text', 'numeric', 'numeric', 'numeric', 'numeric', 'text', 'text']}
                      headings={['Date', 'Impressions', 'Clicks', 'Conversions', 'Revenue', 'CTR', 'CVR']}
                      rows={dailyRows}
                    />
                  ) : (
                    <Text tone="subdued">No data for this period</Text>
                  )}
                </BlockStack>
              </Card>
            </Layout.Section>

            {/* Top Stores */}
            <Layout.Section oneHalf>
              <Card>
                <BlockStack gap="400">
                  <Text variant="headingMd">Top Stores by Revenue</Text>
                  {topStoresRows.length > 0 ? (
                    <DataTable
                      columnContentTypes={['text', 'numeric', 'numeric', 'numeric']}
                      headings={['Store', 'Impressions', 'Conversions', 'Revenue']}
                      rows={topStoresRows}
                    />
                  ) : (
                    <Text tone="subdued">No data</Text>
                  )}
                </BlockStack>
              </Card>
            </Layout.Section>

            {/* Top Bars */}
            <Layout.Section oneHalf>
              <Card>
                <BlockStack gap="400">
                  <Text variant="headingMd">Top Bars by Conversions</Text>
                  {topBarsRows.length > 0 ? (
                    <DataTable
                      columnContentTypes={['text', 'text', 'numeric', 'numeric']}
                      headings={['Bar Name', 'Store', 'Impressions', 'Conversions']}
                      rows={topBarsRows}
                    />
                  ) : (
                    <Text tone="subdued">No data</Text>
                  )}
                </BlockStack>
              </Card>
            </Layout.Section>
          </>
        )}
      </Layout>
    </Page>
  );
}
